import React, { useState, useEffect } from "react";
import { Modal, Button } from "flowbite-react";
import { 
  KeyIcon, 
  LockClosedIcon, 
  ShieldCheckIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";

const ChangePasswordModal = ({ isOpen, onClose, onPasswordChange }) => {
  const { updatePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (currentPassword || newPassword || confirmPassword) {
      setFormTouched(true);
    }
  }, [currentPassword, newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    // Check password strength
    if (passwordStrength < 2) {
      setError("Please choose a stronger password");
      return;
    }

    console.log("Submitting password change request");
    setIsSubmitting(true);
    
    try {
      // Call the updatePassword function from AuthContext
      const response = await updatePassword(currentPassword, newPassword);
      console.log("Password change response:", response);
      
      if (response && response.success) {
        setShowSuccess(true);
        
        setTimeout(() => {
          onPasswordChange();
          onClose();
          
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setPasswordStrength(0);
          setShowSuccess(false);
          setFormTouched(false);
        }, 1500);
      } else {
        setError(response?.message || "Failed to change password. Please try again.");
      }
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to change password. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkPasswordStrength = (password) => {
    if (!password) return 0;
    let strength = 0;
    
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    return Math.min(strength, 4);
  };

  const handlePasswordChange = (e) => {
    const newPass = e.target.value;
    setNewPassword(newPass);
    setPasswordStrength(checkPasswordStrength(newPass));
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return "bg-gray-200 dark:bg-gray-700";
      case 1: return "bg-red-500";
      case 2: return "bg-orange-500";
      case 3: return "bg-yellow-500";
      case 4: return "bg-green-500";
      default: return "bg-gray-200 dark:bg-gray-700";
    }
  };

  const getStrengthText = () => {
    switch (passwordStrength) {
      case 0: return "";
      case 1: return "Weak";
      case 2: return "Fair";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "";
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="md" className="rounded-xl overflow-hidden shadow-2xl">
      <Modal.Header className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 text-gray-800 dark:text-white">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          <h3 className="text-xl font-semibold">Change Password</h3>
        </div>
      </Modal.Header>
      <Modal.Body className="px-8 py-8 bg-white dark:bg-gray-900">
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-10 animate-fadeIn">
            <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-full mb-4 animate-pulse">
              <CheckCircleIcon className="h-14 w-14 text-green-500" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              Password Updated Successfully!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-center">
              Your password has been updated securely.
            </p>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex p-4 mb-6 text-red-800 bg-red-50 dark:bg-red-900/20 dark:text-red-400 border-l-4 border-red-500 rounded-lg shadow-sm animate-fadeIn">
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <form className="space-y-7" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1 block">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    onFocus={() => setFocusedField('current')}
                    onBlur={() => setFocusedField(null)}
                    className={`w-full h-12 pl-10 pr-10 rounded-lg shadow-sm text-gray-800 dark:text-white bg-white dark:bg-gray-800 border transition-colors duration-200 outline-none ${
                      focusedField === 'current' 
                        ? 'border-blue-500 dark:border-blue-400' 
                        : 'border-gray-300 dark:border-gray-700'
                    }`}
                    placeholder="Enter your current password"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <button 
                    type="button" 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150 z-10"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1 block">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={handlePasswordChange}
                    onFocus={() => setFocusedField('new')}
                    onBlur={() => setFocusedField(null)}
                    className={`w-full h-12 pl-10 pr-10 rounded-lg shadow-sm text-gray-800 dark:text-white bg-white dark:bg-gray-800 border transition-colors duration-200 outline-none ${
                      focusedField === 'new' 
                        ? 'border-blue-500 dark:border-blue-400' 
                        : 'border-gray-300 dark:border-gray-700'
                    }`}
                    placeholder="Enter new password"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <button 
                    type="button" 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150 z-10"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                
                {newPassword && (
                  <div className="mt-3 transform transition-all duration-300 ease-in-out">
                    <div className="flex items-center mb-2">
                      <div className="flex h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        {[1, 2, 3, 4].map((segment) => (
                          <div
                            key={segment}
                            className={`h-full w-1/4 transition-all duration-500 ease-out ${
                              passwordStrength >= segment 
                                ? `${getStrengthColor()}` 
                                : "bg-gray-200 dark:bg-gray-700"
                            }`}
                            style={{ 
                              transformOrigin: 'center', 
                              transition: 'all 0.3s ease',
                              transform: passwordStrength >= segment ? 'scaleX(1)' : 'scaleX(0.3)'
                            }}
                          ></div>
                        ))}
                      </div>
                      <span className={`text-xs font-medium ml-3 min-w-[40px] ${
                        passwordStrength <= 1 ? "text-red-500 dark:text-red-400" :
                        passwordStrength === 2 ? "text-orange-500 dark:text-orange-400" :
                        passwordStrength === 3 ? "text-yellow-500 dark:text-yellow-400" :
                        "text-green-500 dark:text-green-400"
                      }`}>
                        {getStrengthText()}
                      </span>
                    </div>
                    <div className="mt-2 text-xs bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <ul className="grid grid-cols-2 gap-2">
                        <li className={`flex items-center ${newPassword.length >= 8 ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 rounded-full ${
                            newPassword.length >= 8 ? 'bg-green-100 dark:bg-green-800/30 text-green-500 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {newPassword.length >= 8 ? '✓' : ''}
                          </span>
                          8+ characters
                        </li>
                        <li className={`flex items-center ${/[A-Z]/.test(newPassword) ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 rounded-full ${
                            /[A-Z]/.test(newPassword) ? 'bg-green-100 dark:bg-green-800/30 text-green-500 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {/[A-Z]/.test(newPassword) ? '✓' : ''}
                          </span>
                          Uppercase
                        </li>
                        <li className={`flex items-center ${/[0-9]/.test(newPassword) ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 rounded-full ${
                            /[0-9]/.test(newPassword) ? 'bg-green-100 dark:bg-green-800/30 text-green-500 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {/[0-9]/.test(newPassword) ? '✓' : ''}
                          </span>
                          Number
                        </li>
                        <li className={`flex items-center ${/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 rounded-full ${
                            /[^A-Za-z0-9]/.test(newPassword) ? 'bg-green-100 dark:bg-green-800/30 text-green-500 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {/[^A-Za-z0-9]/.test(newPassword) ? '✓' : ''}
                          </span>
                          Special char
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1 block">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirm')}
                    onBlur={() => setFocusedField(null)}
                    className={`w-full h-12 pl-10 pr-10 rounded-lg shadow-sm text-gray-800 dark:text-white bg-white dark:bg-gray-800 border transition-colors duration-200 outline-none ${
                      focusedField === 'confirm' 
                        ? 'border-blue-500 dark:border-blue-400'
                        : confirmPassword && newPassword !== confirmPassword
                        ? 'border-red-500 dark:border-red-400'
                        : confirmPassword && newPassword === confirmPassword && confirmPassword.length > 0
                        ? 'border-green-500 dark:border-green-400'
                        : 'border-gray-300 dark:border-gray-700'
                    }`}
                    placeholder="Confirm new password"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <button 
                    type="button" 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150 z-10"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                  {confirmPassword && newPassword === confirmPassword && confirmPassword.length > 0 && (
                    <div className="absolute right-10 top-1/2 transform -translate-y-1/2 z-10">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                    </div>
                  )}
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-2 ml-1">
                    Passwords don't match
                  </p>
                )}
              </div>
            </form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-6 py-3">
        {!showSuccess && (
          <div className="w-full flex justify-end space-x-3">
            <Button 
              color="blue" 
              onClick={handleSubmit} 
              disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || passwordStrength < 2}
              className={`bg-blue-600 dark:bg-blue-500 text-white rounded-lg transition-all duration-200 px-4 py-1.5 text-sm ${
                isSubmitting || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || passwordStrength < 2
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-blue-700 dark:hover:bg-blue-600"
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Updating...</span>
                </div>
              ) : (
                <span>Update Password</span>
              )}
            </Button>
            <Button 
              color="gray" 
              onClick={onClose}
              className="hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 px-4 py-1.5 text-sm"
            >
              Cancel
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePasswordModal; 